import {
  LDContext,
  LDClient,
  initialize as initializeLaunchDarkly,
  LDFlagChangeset,
} from 'launchdarkly-js-client-sdk'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { FeatureFlagKeyName, getFlagDefaults } from '@mobi/settings'
import { AppVersion, OS, MajorVersion } from '@mobi/web-native-comms/web'
import { FeatureFlagsState, mergeFeatures, setFeatures } from './Store'
import { store } from '@core/Store'
import { fullClientVersion } from '@core/Utils/clientVersion'

let ldClient: LDClient

const ANONYMOUS_CONTEXT: LDContext = {
  kind: 'user',
  key: 'Guest',
  anonymous: true,
  custom: getCustomUserAttributes(),
}

interface LaunchDarklyInitializeProps {
  clientId: string
  bootstrapAccountNumber: string | undefined
  bootstrapClientFlags: FeatureFlagsState
  bootstrapServerFlags: FeatureFlagsState
}

const initialize = ({
  clientId,
  bootstrapAccountNumber,
  bootstrapClientFlags,
  bootstrapServerFlags,
}: LaunchDarklyInitializeProps): Promise<void> => {
  return new Promise(resolve => {
    if (ldClient) {
      return
    }

    if (!bootstrapClientFlags) {
      bootstrapClientFlags = {}
    }

    // Optionally initialize state, pending retrieval of flags from the Launch Darkly API)
    if (bootstrapServerFlags) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
      const { $valid, $flagsState, ...initialFlags } = bootstrapServerFlags as any
      // remove null/undefined value from server bootstrap object
      for (const propName in initialFlags) {
        if (initialFlags[propName] === null || initialFlags[propName] === undefined) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete initialFlags[propName]
        }
      }
      store.dispatch(
        setFeatures({
          features: { ...bootstrapClientFlags, ...initialFlags },
          isUserAnonymous: !bootstrapAccountNumber,
        })
      )
    }

    let context: LDContext = ANONYMOUS_CONTEXT

    if (bootstrapAccountNumber) {
      context = {
        key: bootstrapAccountNumber,
        custom: getSimpleCustomUserAttributes(),
      }
    }

    ldClient = initializeLaunchDarkly(clientId, context, {
      application: {
        id: 'tabtouch-mobi-webapp',
        version: fullClientVersion,
      },
      streaming: true,
      sendEventsOnlyForVariation: true,
    })

    ldClient.on('ready', async () => {
      const flags = Object.keys(bootstrapClientFlags).reduce(
        (accumulator, flag) => {
          accumulator[flag] = ldClient.variation(
            flag,
            bootstrapClientFlags[flag as FeatureFlagKeyName]
          )
          return accumulator
        },
        {} as Record<string, unknown>
      )

      const accountNumber = (await userAccountState$.take(1).toPromise()).accountNumber

      store.dispatch(setFeatures({ features: flags, isUserAnonymous: !accountNumber }))
      subscribeToLaunchDarklyChanges()
      subscribeToUserStateChanges()
      resolve()
    })
  })
}

const subscribeToLaunchDarklyChanges = () => {
  ldClient.on('change', async (changeset: LDFlagChangeset) => {
    const features = Object.keys(changeset).reduce(
      (accumulator, key) => {
        accumulator[key] = changeset[key].current
        return accumulator
      },
      {} as Record<string, unknown>
    )

    store.dispatch(mergeFeatures(features))
  })
}

const subscribeToUserStateChanges = () => {
  userAccountState$
    .map(record => ({
      accountNumber: record.accountNumber,
      isLoggedIn: record.isLoggedIn,
    }))
    .distinctUntilChanged()
    .subscribe(async ({ accountNumber, isLoggedIn }) => {
      if (isLoggedIn === null) {
        return
      }

      const ldContext: LDContext = accountNumber
        ? { kind: 'user', key: accountNumber.toString(), custom: getCustomUserAttributes() }
        : ANONYMOUS_CONTEXT

      const updatedFlags = await ldClient.identify(ldContext)

      store.dispatch(setFeatures({ features: updatedFlags, isUserAnonymous: !accountNumber }))
    })
}

export const initializeLaunchDarklyServiceWithConfig = (
  clientId: string,
  accountNumber: number | null
): Promise<void> => {
  const serverFlags = window.LDFlags
  return initialize({
    clientId,
    bootstrapAccountNumber: accountNumber?.toString() ?? '',
    bootstrapClientFlags: getFlagDefaults(),
    bootstrapServerFlags: serverFlags,
  })
}

function getSimpleCustomUserAttributes() {
  return {
    clientVersion: Number.parseFloat(`${VERSION_MAJOR}.${VERSION_MINOR}`),
    nativeAppVersion: AppVersion || 0,
    hostName: location.hostname,
  }
}

function getCustomUserAttributes() {
  return {
    ...getSimpleCustomUserAttributes(),
    os: OS,
    systemMajorVersion: MajorVersion,
  }
}
